import React, { Suspense, useCallback, useEffect, useState } from 'react';

import Link from 'next/link';

import WithAuth from '@repo/editor/src/components/WithAuth/WithAuth';
import { withEventScope } from '@repo/event-processor';
import { Button, secondarySmallTheme } from '@repo/ui/components/Buttons';
import { IconEventWrapper } from '@repo/ui/components/Icon';
import ChevronLeftIcon from '@repo/ui/components/Icon/ChevronLeftIcon';
import ChevronRightIcon from '@repo/ui/components/Icon/ChevronRightIcon';
import { themeStyle } from '@repo/ui/theming';

import AuthenticationButtonsWithSearchParams from 'components/HeadMenu/AuthenticationButtonsWithSearchParams';
import SideMenuSearch from 'components/HeadMenu/SideMenu/SideMenuSearch';
import Logo from 'components/Logo/Logo';

import HeaderLayout from '../HeaderLayout/HeaderLayout';

import {
  AuthButtonsContainer,
  Backdrop,
  Content,
  LinksColumnLabel,
  LinkWrapper,
  LogoContainer,
  MenuItemsWrapper,
  SideMenuDivider,
  Wrapper,
} from './styles';
import { SideMenuProps } from './types';

const iconTheme = {
  color: themeStyle.varInkMain,
  activeColor: themeStyle.varInkMain,
  activeDefaultColor: themeStyle.varInkMain,
};

const SideMenu: React.FC<SideMenuProps> = ({
  isOpen,
  onClose,
  onSignup,
  navigationItems,
}) => {
  const [state, setState] = useState({
    isOpen: isOpen,
    closing: false,
  });

  const [selectedMenu, setSelectedMenu] = useState<string | null>();

  const open = useCallback(() => {
    if (state.isOpen && !state.closing) {
      return;
    }
    setState({
      isOpen: true,
      closing: false,
    });
  }, [state]);

  const close = useCallback(() => {
    if (!state.isOpen || state.closing) {
      return;
    }
    onClose?.();
    setState({
      isOpen: true,
      closing: true,
    });
    setSelectedMenu(null);
  }, [onClose, state]);

  const onEndTransition = (): void => {
    if (state.closing) {
      setState({
        closing: false,
        isOpen: false,
      });
    }
  };

  useEffect(() => {
    isOpen ? open() : close();
  }, [isOpen, close, open]);

  const onMenuSelect = (label: string): void => {
    setSelectedMenu(label);
  };

  return (
    <>
      {state.isOpen && (
        <Backdrop
          state={state}
          onClick={close}
          onTransitionEnd={onEndTransition}
        />
      )}
      <Wrapper isOpen={state.isOpen && !state.closing}>
        {selectedMenu ? (
          <>
            <IconEventWrapper activeOnHover theme={iconTheme}>
              <LinkWrapper onClick={(): void => setSelectedMenu(null)}>
                <ChevronLeftIcon height="8px" theme={iconTheme} />
                <LinksColumnLabel>{selectedMenu}</LinksColumnLabel>
              </LinkWrapper>
            </IconEventWrapper>
            <SideMenuDivider
              color={themeStyle.varInkSuperLight}
              colorDark={themeStyle.varInkLight}
            />
          </>
        ) : (
          <LogoContainer>
            <Logo height="36px" href="/" width="91px" />
          </LogoContainer>
        )}
        {/* we remove it from the page when side panel is closed to reset the state */}
        {!selectedMenu && state.isOpen && (
          <Suspense>
            <SideMenuSearch isOpen={state.isOpen} />
          </Suspense>
        )}
        <Content>
          {navigationItems.map((item) => {
            return (
              <MenuItemsWrapper
                key={item.label}
                visible={selectedMenu === item.label}
              >
                <HeaderLayout
                  navigationItems={item.type === 'table' ? item.items : []}
                  onClick={close}
                />
              </MenuItemsWrapper>
            );
          })}
          {!selectedMenu &&
            navigationItems.map((item) => {
              return (
                <div key={item.label}>
                  {item.type === 'link' ? (
                    <LinkWrapper>
                      <Link
                        href={item.path}
                        style={{ width: '100%' }}
                        onClick={close}
                      >
                        <LinksColumnLabel>{item.label}</LinksColumnLabel>
                      </Link>
                    </LinkWrapper>
                  ) : (
                    <IconEventWrapper activeOnHover fitParent theme={iconTheme}>
                      <LinkWrapper
                        onClick={(): void => onMenuSelect(item.label)}
                      >
                        <LinksColumnLabel>{item.label}</LinksColumnLabel>
                        <ChevronRightIcon height="8px" theme={iconTheme} />
                      </LinkWrapper>
                    </IconEventWrapper>
                  )}
                  <SideMenuDivider
                    color={themeStyle.varInkSuperLight}
                    colorDark={themeStyle.varInkLight}
                  />
                </div>
              );
            })}
        </Content>
        <AuthButtonsContainer>
          <WithAuth
            renderAuth={(_, onLogOut) => (
              <Button
                height="30px"
                label="Sign Out"
                name="sideMenuSignOutButton"
                theme={secondarySmallTheme}
                width="100%"
                onClick={onLogOut}
              />
            )}
            renderUnauth={() => (
              <AuthenticationButtonsWithSearchParams
                layout="column"
                onSignup={onSignup}
              />
            )}
          />
        </AuthButtonsContainer>
      </Wrapper>
    </>
  );
};

export default withEventScope(SideMenu, { module: 'sideMenu' });
